import Dialog from '@anm/components/modals/Dialog';
import AlertBlock from '@anm/components/notification/AlertBlock';
import ConfirmDelete from '@anm/components/notification/ConfirmDelete';
import useEventPending from '@anm/hooks/useEventPending';
import noop from 'lodash/fp/noop';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import withVideoFolder, { WithVideoFolder } from '../../../../HOCs/withVideoFolder';
import { videoFolderSelectors } from '../../../../store/modules/videoFolder';

type HOCs = WithVideoFolder;
export type DeleteFolderModalProps = {
  folderId: number;
  onDelete: () => void;
  onClose?: () => void;
};
type DeleteFolderHandler = {
  onDeleteFolder(): void;
};

const DeleteAlert: FC<HOCs & DeleteFolderModalProps> = ({ onClose }) => (
  <AlertBlock
    button={{
      text: 'Ok',
      size: 'upper-medium',
      variant: 'blue-heavy-border'
    }}
    title="This folder is not empty"
    titleSize="medium"
    description="If you want to delete the folder, please first delete or move all the contents from it."
    onButtonClick={onClose}
  />
);

const ConfirmFolderDelete: FC<HOCs & DeleteFolderModalProps & DeleteFolderHandler> = ({
  videoFolder,
  onDeleteFolder,
  onClose,
  onDelete
}) => {
  const statEventPending = useEventPending(videoFolder.deleteFolder.isPending, onDelete);

  return (
    <ConfirmDelete
      isPending={videoFolder.deleteFolder.isPending}
      deleteItemName="folder"
      onClose={onClose}
      onDelete={() => {
        onDeleteFolder();
        statEventPending();
      }}
    />
  );
};

const Notification: FC<HOCs & DeleteFolderModalProps & { onDeleteFolder(): void }> = props => {
  const { folderId } = props;
  const isFolderEmpty = useSelector(videoFolderSelectors.isFolderEmpty);
  const isFolderExist = useSelector(videoFolderSelectors.isFolderExist);

  if (!isFolderExist(folderId)) return null;

  return <>{isFolderEmpty(folderId) ? <ConfirmFolderDelete {...props} /> : <DeleteAlert {...props} />}</>;
};

const DeleteFolderModal: FC<HOCs & DeleteFolderModalProps> = props => {
  const deleteFolder = () => props.deleteFolder({ folderId: props.folderId });

  return (
    <Dialog onModalClose={props.onClose || noop} onSubmit={deleteFolder}>
      <Notification {...props} onDeleteFolder={deleteFolder} />
    </Dialog>
  );
};

export default withVideoFolder(DeleteFolderModal);
